// Simple UUID generator using browser's built-in crypto API
const generateId = (): string => {
  return crypto.randomUUID()
}

// Types for analytics
export type EventType = 'page_view' | 'click' | 'conversion'
export type FunnelPath =
  | 'advertorial_feet_ankle'
  | 'advertorial_swollen_legs'
  | 'advertorial_neck_shoulders'
  | 'advertorial_upper_lower_back'
  | 'advertorial_knee_joints'
  | 'advertorial_hands_wrists'
  | 'advertorial_hips_glutes'
  | 'sales_neck_shoulders'
  | 'sales_upper_lower_back'
  | 'sales_knee_joints'
  | 'sales_hands_wrists'
  | 'sales_swollen_legs'
  | 'sales_hips_glutes'
  | 'sales_feet_ankles'
  | 'direct_landing'
  | 'options_page'
  | 'checkout'
  | 'other'

export interface AnalyticsEvent {
  id: string
  timestamp: number
  type: EventType
  path: string // URL path
  funnelPath: FunnelPath
  pageName: string // Human-readable page name
  metadata?: Record<string, any>
  sessionId: string
}

export interface FunnelMetrics {
  views: number
  clicks: number
  conversions: number
  ctr: number // Click Through Rate (%)
  conversionRate: number // Conversion Rate (%)
}

export interface PageMetrics extends FunnelMetrics {
  pageName: string
  path: string
}

// Storage keys
const STORAGE_KEY = 'ginext_analytics_events'
const SESSION_KEY = 'ginext_analytics_session'

// Helper to get or create session ID
export const getSessionId = (): string => {
  let sessionId = localStorage.getItem(SESSION_KEY)
  if (!sessionId) {
    sessionId = generateId()
    localStorage.setItem(SESSION_KEY, sessionId)
  }
  return sessionId
}

// Helper to get human-readable page name from path
export const getPageName = (pathname: string): string => {
  const pathMap: Record<string, string> = {
    '/': 'Options Page',
    '/options': 'Options Page',
    '/home': 'Landing Page (Home)',
    '/home-b': 'Landing Page (Home B)',
    '/feet&ankles': 'Sales: Feet & Ankles',
    '/NECK&SHOULDERS': 'Sales: Neck & Shoulders',
    '/LOWER&UPPERBACK': 'Sales: Upper & Lower Back',
    '/KNEES&JOINTS': 'Sales: Knee Joints',
    '/HANDS&WRISTS': 'Sales: Hands & Wrists',
    '/SWOLLEN-LEGS': 'Sales: Swollen Legs',
    '/HIPS&GLUTES': 'Sales: Hips & Glutes',
    '/ADVERT-FEET&ANKLE': 'Advertorial: Feet & Ankles',
    '/ADVERT-SWOLLEN-LEGS': 'Advertorial: Swollen Legs',
    '/ADVERT-NECK&SHOULDERS': 'Advertorial: Neck & Shoulders',
    '/ADVERT-UPPER&LOWERBACK': 'Advertorial: Upper & Lower Back',
    '/ADVERT-KNEES-JOINTS': 'Advertorial: Knee Joints',
    '/ADVERT-HANDS&WRIST': 'Advertorial: Hands & Wrists',
    '/ADVERT-HIPS&GLUTES': 'Advertorial: Hips & Glutes',
    '/checkout': 'Checkout',
  }
  return pathMap[pathname] || pathname
}

// Helper to determine funnel path based on URL
export const determineFunnelPath = (pathname: string): FunnelPath => {
  // Advertorial pages
  if (pathname.includes('ADVERT-FEET&ANKLE')) return 'advertorial_feet_ankle'
  if (pathname.includes('ADVERT-SWOLLEN-LEGS'))
    return 'advertorial_swollen_legs'
  if (pathname.includes('ADVERT-NECK&SHOULDERS'))
    return 'advertorial_neck_shoulders'
  if (pathname.includes('ADVERT-UPPER&LOWERBACK'))
    return 'advertorial_upper_lower_back'
  if (pathname.includes('ADVERT-KNEES-JOINTS')) return 'advertorial_knee_joints'
  if (pathname.includes('ADVERT-HANDS&WRIST')) return 'advertorial_hands_wrists'
  if (pathname.includes('ADVERT-HIPS&GLUTES')) return 'advertorial_hips_glutes'

  // Sales pages
  if (pathname.includes('feet&ankles')) return 'sales_feet_ankles'
  if (pathname.includes('NECK&SHOULDERS')) return 'sales_neck_shoulders'
  if (pathname.includes('LOWER&UPPERBACK')) return 'sales_upper_lower_back'
  if (pathname.includes('KNEES&JOINTS')) return 'sales_knee_joints'
  if (pathname.includes('HANDS&WRISTS')) return 'sales_hands_wrists'
  if (pathname.includes('SWOLLEN-LEGS')) return 'sales_swollen_legs'
  if (pathname.includes('HIPS&GLUTES')) return 'sales_hips_glutes'

  // Other pages
  if (pathname === '/' || pathname === '/options') return 'options_page'
  if (pathname === '/home' || pathname === '/home-b') return 'direct_landing'
  if (pathname === '/checkout') return 'checkout'

  return 'other'
}

// Track an event
export const trackEvent = (type: EventType, metadata?: Record<string, any>) => {
  try {
    const events = getEvents()
    const pathname = window.location.pathname
    const newEvent: AnalyticsEvent = {
      id: generateId(),
      timestamp: Date.now(),
      type,
      path: pathname,
      funnelPath: determineFunnelPath(pathname),
      pageName: getPageName(pathname),
      metadata,
      sessionId: getSessionId(),
    }

    events.push(newEvent)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events))

    // Log for debugging
    console.log(`[Analytics] Tracked ${type} on ${newEvent.pageName}`, newEvent)
  } catch (error) {
    console.error('Failed to track analytics event:', error)
  }
}

// Get all events
export const getEvents = (): AnalyticsEvent[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch (error) {
    console.error('Failed to retrieve analytics events:', error)
    return []
  }
}

// Clear all data
export const clearAnalyticsData = () => {
  localStorage.removeItem(STORAGE_KEY)
  console.log('[Analytics] Data cleared')
}

// Calculate metrics for individual pages
export const calculatePageMetrics = (
  events: AnalyticsEvent[],
  startDate: number,
  endDate: number,
): PageMetrics[] => {
  const filteredEvents = events.filter(
    (e) => e.timestamp >= startDate && e.timestamp <= endDate,
  )

  // Group events by page path
  const pageGroups = new Map<string, AnalyticsEvent[]>()
  filteredEvents.forEach((event) => {
    const existing = pageGroups.get(event.path) || []
    existing.push(event)
    pageGroups.set(event.path, existing)
  })

  // Calculate metrics for each page
  const pageMetrics: PageMetrics[] = []
  pageGroups.forEach((pageEvents, path) => {
    const views = pageEvents.filter((e) => e.type === 'page_view').length
    const clicks = pageEvents.filter((e) => e.type === 'click').length
    const conversions = pageEvents.filter((e) => e.type === 'conversion').length

    pageMetrics.push({
      pageName: pageEvents[0].pageName,
      path,
      views,
      clicks,
      conversions,
      ctr: views > 0 ? (clicks / views) * 100 : 0,
      conversionRate: clicks > 0 ? (conversions / clicks) * 100 : 0,
    })
  })

  // Sort by views descending
  return pageMetrics.sort((a, b) => b.views - a.views)
}

// Calculate metrics for a specific date range (legacy funnel view)
export const calculateMetrics = (
  events: AnalyticsEvent[],
  startDate: number,
  endDate: number,
): Record<FunnelPath, FunnelMetrics> => {
  // Filter events by date range
  const filteredEvents = events.filter(
    (e) => e.timestamp >= startDate && e.timestamp <= endDate,
  )

  // Initialize metrics for each path
  const allPaths: FunnelPath[] = [
    'advertorial_feet_ankle',
    'advertorial_swollen_legs',
    'advertorial_neck_shoulders',
    'advertorial_upper_lower_back',
    'advertorial_knee_joints',
    'advertorial_hands_wrists',
    'advertorial_hips_glutes',
    'sales_neck_shoulders',
    'sales_upper_lower_back',
    'sales_knee_joints',
    'sales_hands_wrists',
    'sales_swollen_legs',
    'sales_hips_glutes',
    'sales_feet_ankles',
    'direct_landing',
    'options_page',
    'checkout',
    'other',
  ]

  const metrics: Record<FunnelPath, FunnelMetrics> = {} as Record<
    FunnelPath,
    FunnelMetrics
  >
  allPaths.forEach((path) => {
    metrics[path] = {
      views: 0,
      clicks: 0,
      conversions: 0,
      ctr: 0,
      conversionRate: 0,
    }
  })

  // Aggregate counts
  filteredEvents.forEach((event) => {
    const pathMetrics = metrics[event.funnelPath]
    if (!pathMetrics) return

    if (event.type === 'page_view') {
      pathMetrics.views++
    } else if (event.type === 'click') {
      pathMetrics.clicks++
    } else if (event.type === 'conversion') {
      pathMetrics.conversions++
    }
  })

  // Calculate rates
  Object.keys(metrics).forEach((key) => {
    const path = key as FunnelPath
    const m = metrics[path]

    m.ctr = m.views > 0 ? (m.clicks / m.views) * 100 : 0
    m.conversionRate = m.clicks > 0 ? (m.conversions / m.clicks) * 100 : 0
  })

  return metrics
}
